/**
 * BDC Agent dashboard.
 *
 * The BDC Agent works the lead QUEUE — they're not pinned to their own
 * assignments the way a Sales Consultant is. Their scorecard is therefore
 * about dealership-wide queue health and first-response speed, not personal
 * lead/appointment counts.
 *
 * Backend fills {@code snapshot.bdc} with the metrics BDC are graded on:
 * unassigned-lead queue, hot leads without a next step, appointments booked
 * dealership-wide today, and the trailing-window first-response SLA
 * (p50 / p90 in minutes).
 */

import KpiTile from '../../components/dashboard/KpiTile';
import { ColumnChart } from '../../components/dashboard/Pictorial';

/** Pretty-print a minutes value — "4m" / "1h 12m" / "—". */
function fmtMins(mins) {
  if (mins == null) return '—';
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins - h * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function BDCAgentDashboard({ snapshot, periodLabel }) {
  const b = snapshot?.bdc;

  const p50OverBenchmark = b?.firstResponseMinutesP50 != null && b.firstResponseMinutesP50 > 1.5;
  const p90OverBenchmark = b?.firstResponseMinutesP90 != null && b.firstResponseMinutesP90 > 15;

  return (
    <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── ROW 1 — the queue ── */}
      <div className="grid-4">
        <KpiTile index={0} label="UNASSIGNED QUEUE" periodLabel="Now" to="/leads?filter=unassigned"
          value={b?.unassignedLeadsOpen ?? '—'}
          color={(b?.unassignedLeadsOpen ?? 0) > 0 ? 'var(--warning)' : 'var(--success)'}
          tone={(b?.unassignedLeadsOpen ?? 0) > 0 ? 'warning' : undefined}
          sub="waiting for an owner" />
        <KpiTile index={1} label="HOT · NO NEXT STEP" periodLabel="Now" to="/leads?filter=hot-no-next"
          value={b?.hotNoNextStep ?? '—'}
          color={(b?.hotNoNextStep ?? 0) > 0 ? 'var(--danger)' : 'var(--success)'}
          tone={(b?.hotNoNextStep ?? 0) > 0 ? 'danger' : undefined}
          sub="high-score lead with no task" />
        <KpiTile index={2} label="BOOKED TODAY" periodLabel="Today" to="/appointments"
          value={b?.appointmentsCreatedToday ?? '—'}
          sub="appointments created today" />
        <KpiTile index={3} label="FIRST-RESPONSE p50" periodLabel={periodLabel}
          value={fmtMins(b?.firstResponseMinutesP50)}
          color={p50OverBenchmark ? 'var(--warning)' : 'var(--success)'}
          tone={p50OverBenchmark ? 'warning' : undefined}
          sub="median mins to first outbound" />
      </div>

      {/* ── ROW 1b — show rate (the client's card names it explicitly) ── */}
      <div className="grid-4">
        <KpiTile index={0} label="SHOW RATE" periodLabel={periodLabel}
          value={b?.showRate == null ? '—' : `${Math.round(b.showRate * 100)}%`}
          color={b?.showRate != null && b.showRate < 0.7 ? 'var(--warning)' : 'var(--success)'}
          tone={b?.showRate != null && b.showRate < 0.7 ? 'warning' : undefined}
          sub={b ? `${b.appointmentsAttendedWindow ?? 0} attended · ${b.appointmentsNoShowWindow ?? 0} no-shows` : undefined} />
        <KpiTile index={1} label="ATTENDED" periodLabel={periodLabel}
          value={b?.appointmentsAttendedWindow ?? '—'} />
        <KpiTile index={2} label="NO-SHOWS" periodLabel={periodLabel}
          value={b?.appointmentsNoShowWindow ?? '—'}
          color={(b?.appointmentsNoShowWindow ?? 0) > 0 ? 'var(--danger)' : 'var(--success)'}
          sub="chase these back in" />
        <KpiTile index={3} label="FIRST-RESPONSE p90" periodLabel={periodLabel}
          value={fmtMins(b?.firstResponseMinutesP90)}
          color={p90OverBenchmark ? 'var(--danger)' : 'var(--success)'}
          tone={p90OverBenchmark ? 'danger' : undefined}
          sub="9 of 10 leads answered within" />
      </div>

      {/* ── ROW 2 — SLA p90 + sketch ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>FIRST-RESPONSE SLA · {String(periodLabel).toUpperCase()}</div>
          <ColumnChart height={160} bars={[
            { label: 'Median (p50)', value: Math.round((b?.firstResponseMinutesP50 ?? 0) * 10) / 10,
              color: p50OverBenchmark ? 'var(--warning)' : 'var(--success)' },
            { label: '90th pct',     value: Math.round((b?.firstResponseMinutesP90 ?? 0) * 10) / 10,
              color: p90OverBenchmark ? 'var(--danger)' : 'var(--accent-primary)' },
          ]} />
          <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>
            minutes · lower is better
          </div>
        </div>
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>WHAT TO WORK ON NOW</div>
          <ColumnChart height={160} bars={[
            { label: 'Queue', value: b?.unassignedLeadsOpen ?? 0, color: 'var(--warning)' },
            { label: 'Hot · no step', value: b?.hotNoNextStep ?? 0, color: 'var(--danger)' },
            { label: 'Booked today',  value: b?.appointmentsCreatedToday ?? 0, color: 'var(--success)' },
          ]} />
        </div>
      </div>

      {!b && (
        <div className="card">
          <div className="mono text-xs text-muted">Loading your BDC board…</div>
        </div>
      )}
    </div>
  );
}
